import React, { useState, useMemo } from 'react';
import camelcase from 'camelcase-es5';
import TPhoneNumberTextField from './@types/PhoneNumberTextField';
import { Dropdown } from '../Dropdown';
import TextFieldBase from './TextFieldBase';
import { countryDialCodesDropDownOptions } from './countryDialCodes';
import useFormControlReducer from '../../hooks/useFormControlReducer';
import TextFieldFormControl from './TextFieldFormControl';
import TextFieldInput from './TextFieldInput';
import InputLabel from '../InputLabel';
import TextFieldFooter from './TextFieldFooter';

const generatePhoneTextFieldId = (labelText: string) => `PhoneTextField-${camelcase(labelText).replace(/\s+/g, '')}`;

const PhoneNumberTextField = ({
  errorText,
  defaultCountryCode = 'NL',
  value = '',
  onChange = () => {},
  onFocus,
  onBlur,
  onMouseOut,
  onMouseOver,
  onKeyPress,
  isRequired,
  PhoneNumberLabel = 'Phone number',
  optionalText,
  maxLength = 0,
  inputRef,
  isLive = true,
}: TPhoneNumberTextField) => {
  const dropdownOptions = useMemo(() => countryDialCodesDropDownOptions({ defaultCountryCode }), [defaultCountryCode]);

  const [countryCode, setCountryCode] = useState(defaultCountryCode);
  const [phoneNumber, setPhoneNumber] = useState(value);

  const { state, handlers } = useFormControlReducer({
    onFocus,
    onBlur,
    onMouseOut,
    onMouseOver,
    errorText,
    value: phoneNumber,
  });

  const { isDirty, isEmpty, isErrored, isFocused } = state;
  const { handleFocus, handleBlur, handleMouseOver, handleMouseOut } = handlers;

  const phoneNumberTextFieldId = generatePhoneTextFieldId(PhoneNumberLabel);

  const handleInputChange =
    (type: 'country' | 'phone') => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const newValue = e.target.value;
      let updatedCountryCode = countryCode;
      let updatedPhoneNumber = phoneNumber;

      if (type === 'country') {
        setCountryCode(newValue);
        updatedCountryCode = newValue;
      } else {
        setPhoneNumber(newValue);
        updatedPhoneNumber = newValue;
      }

      // Pass only one `onChange` event
      const event = {
        target: {
          value: `${updatedCountryCode} ${updatedPhoneNumber}`,
        },
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    };

  return (
    <TextFieldBase kind="phoneNumber">
      {PhoneNumberLabel && (
        <InputLabel
          htmlFor={phoneNumberTextFieldId}
          isInputRequired={isRequired}
          labelText={PhoneNumberLabel}
          optionalText={optionalText}
        />
      )}

      <div className="hz-PhoneNumberTextFieldWrapper">
        <Dropdown
          value={countryCode}
          isRequired={isRequired}
          options={dropdownOptions}
          className="hz-DialCodesDropdown"
          id={phoneNumberTextFieldId}
          onChange={handleInputChange('country')}
          onBlur={handleBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
        />
        <div className="hz-PhoneNumberTextField">
          <TextFieldFormControl isFocused={isFocused} isErrored={isErrored} isDirty={isDirty} isEmpty={isEmpty}>
            <TextFieldInput
              type="tel"
              value={phoneNumber}
              isRequired={isRequired}
              id={phoneNumberTextFieldId}
              onChange={handleInputChange('phone')}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onMouseOver={handleMouseOver}
              onMouseOut={handleMouseOut}
              onKeyPress={onKeyPress}
              ref={inputRef}
            />
          </TextFieldFormControl>
        </div>
      </div>
      <TextFieldFooter
        errorText={errorText}
        maxLength={maxLength}
        isFocused={isFocused}
        isRequired={isRequired}
        isLive={isLive}
      />
    </TextFieldBase>
  );
};

export default PhoneNumberTextField;
